import { useEffect, useRef, useState } from 'react'
import { ChevronDown, ChevronRight, Play } from 'lucide-react'
import { useDailyReviewStore } from '@/stores/dailyReview'
import type { ReviewAsset, VariantFeedback, VariantReviewInput } from '@/types'
import { CHANGE_OPTIONS, CHANGE_LABEL, PCT_OPTIONS, formatBytes } from './constants'

/**
 * Click-to-play video: only a poster image (or gray tile) is shown until the
 * user clicks ▶ — no <video> element exists before that, so nothing is
 * fetched from S3 for videos the reviewer never opens.
 *
 * 预加载增强：若该视频已被「预加载本页视频」缓存为 Blob，则播放直接使用
 * blob URL（秒开、不占网络）；未缓存的保持原有流式播放，且播放期间向
 * store 上报，让预加载队列让路。
 */
function PosterVideo({ assetId, src, poster }: { assetId: string; src: string; poster: string | null }) {
  // 点击播放那一刻锁定播放源：预加载随后完成也不切换 src（否则视频会从头重播）
  const [activeSrc, setActiveSrc] = useState<string | null>(null)
  const blobUrl = useDailyReviewStore((s) => s.videoBlobs[assetId])
  const setPlaybackActive = useDailyReviewStore((s) => s.setPlaybackActive)
  // 流式播放（非 blob）期间向 store 计数，预加载队列让路；blob 播放不占网络
  const streamingRef = useRef(false)
  const isBlobPlayback = activeSrc?.startsWith('blob:') ?? false

  const markStreaming = (active: boolean) => {
    if (isBlobPlayback) return
    if (active && !streamingRef.current) {
      streamingRef.current = true
      setPlaybackActive(1)
    } else if (!active && streamingRef.current) {
      streamingRef.current = false
      setPlaybackActive(-1)
    }
  }

  // 卸载兜底：确保计数归还
  useEffect(
    () => () => {
      if (streamingRef.current) {
        streamingRef.current = false
        setPlaybackActive(-1)
      }
    },
    [setPlaybackActive],
  )

  if (activeSrc) {
    return (
      <video
        controls
        autoPlay
        preload="auto"
        className="w-full rounded-md bg-black"
        src={activeSrc}
        onPlaying={() => markStreaming(true)}
        onWaiting={() => markStreaming(true)}
        onPause={() => markStreaming(false)}
        onEnded={() => markStreaming(false)}
        onError={() => markStreaming(false)}
      />
    )
  }

  return (
    <button
      type="button"
      onClick={() => setActiveSrc(blobUrl ?? src)}
      className="relative block w-full aspect-[9/16] rounded-md overflow-hidden bg-gray-200 group"
      title={blobUrl ? 'Cached — instant play' : 'Click to load and play'}
    >
      {poster ? (
        <img src={poster} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-b from-gray-100 to-gray-300" />
      )}
      {blobUrl && (
        <span className="absolute top-1.5 right-1.5 rounded bg-emerald-600/90 px-1.5 py-0.5 text-[10px] font-medium text-white">
          已缓存
        </span>
      )}
      <span className="absolute inset-0 flex items-center justify-center">
        <span className="flex items-center justify-center h-12 w-12 rounded-full bg-black/50 group-hover:bg-black/70 transition-colors">
          <Play className="h-6 w-6 text-white fill-white ml-0.5" />
        </span>
      </span>
    </button>
  )
}

interface VariantPanelProps {
  variant: number
  passFail: string | null
  canonical: ReviewAsset | null
  rawVariants: ReviewAsset[]
  myReview: VariantReviewInput
  otherReviews: VariantFeedback[]
  onChange: (review: VariantReviewInput) => void
}

export function VariantPanel({
  variant,
  passFail,
  canonical,
  rawVariants,
  myReview,
  otherReviews,
  onChange,
}: VariantPanelProps) {
  const [showRaw, setShowRaw] = useState(false)

  const toggleChange = (key: string) => {
    const changes = myReview.changes.includes(key)
      ? myReview.changes.filter((c) => c !== key)
      : [...myReview.changes, key]
    onChange({ ...myReview, changes })
  }

  return (
    <div className="flex-1 min-w-[200px] max-w-[300px] rounded-lg border border-gray-200 p-3 space-y-3">
      <div className="flex items-center justify-between">
        <span className="font-medium text-sm">variant_{variant}</span>
        {passFail && (
          <span
            className={`text-xs font-semibold px-2 py-0.5 rounded ${
              passFail === 'PASS' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
            }`}
          >
            {passFail}
          </span>
        )}
      </div>

      {canonical?.preview_url ? (
        <PosterVideo assetId={canonical.id} src={canonical.preview_url} poster={canonical.thumbnail_url} />
      ) : (
        <div className="aspect-[9/16] w-full rounded-md bg-gray-50 flex items-center justify-center text-xs text-gray-400">
          no canonical video
        </div>
      )}
      {canonical?.download_url && (
        <a
          href={canonical.download_url}
          className="text-xs text-blue-600 hover:underline"
        >
          Download mp4 {formatBytes(canonical.file_size)}
        </a>
      )}

      {rawVariants.length > 0 && (
        <div>
          <button
            type="button"
            onClick={() => setShowRaw(!showRaw)}
            className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
          >
            {showRaw ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
            Raw takes &amp; retries ({rawVariants.length}) — for mash-ups
          </button>
          {showRaw && (
            <ul className="mt-2 space-y-1">
              {rawVariants.map((raw) => (
                <li key={raw.id} className="text-xs flex items-center justify-between gap-2">
                  <span className="truncate text-gray-600" title={`${raw.run_id ?? ''}/${raw.filename}`}>
                    {raw.filename}
                    {raw.retry > 0 && ` (retry ${raw.retry})`}
                  </span>
                  <span className="flex gap-2 shrink-0">
                    {raw.preview_url && (
                      <a href={raw.preview_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">
                        play
                      </a>
                    )}
                    {raw.download_url && (
                      <a href={raw.download_url} className="text-blue-600 hover:underline">
                        get
                      </a>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Review controls */}
      <div className="border-t pt-2 space-y-2">
        <div className="flex gap-2">
          {(['use', 'drop'] as const).map((rating) => (
            <button
              key={rating}
              type="button"
              onClick={() => onChange({ ...myReview, rating: myReview.rating === rating ? null : rating })}
              className={`px-3 py-1 rounded text-xs font-medium border ${
                myReview.rating === rating
                  ? rating === 'use'
                    ? 'bg-green-600 text-white border-green-600'
                    : 'bg-red-600 text-white border-red-600'
                  : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
              }`}
            >
              {rating === 'use' ? '✓ use' : '✗ drop'}
            </button>
          ))}
        </div>

        {myReview.rating === 'use' && (
          <>
            <div className="flex items-center gap-2 text-xs">
              <span className="text-gray-500 shrink-0">后期加工</span>
              {PCT_OPTIONS.map((pct) => (
                <button
                  key={pct}
                  type="button"
                  onClick={() => onChange({ ...myReview, post_processing_pct: pct })}
                  className={`px-1.5 py-0.5 rounded border ${
                    myReview.post_processing_pct === pct
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {pct}%
                </button>
              ))}
            </div>
            <div className="flex flex-wrap gap-1">
              {CHANGE_OPTIONS.map(({ key, label }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => toggleChange(key)}
                  className={`px-1.5 py-0.5 rounded text-xs border ${
                    myReview.changes.includes(key)
                      ? 'bg-amber-100 border-amber-400 text-amber-800'
                      : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </>
        )}

        <textarea
          value={myReview.note ?? ''}
          onChange={(e) => onChange({ ...myReview, note: e.target.value })}
          placeholder="comment…"
          rows={2}
          className="w-full text-xs border border-gray-200 rounded p-1.5 resize-y"
        />
      </div>

      {/* Other reviewers */}
      {otherReviews.length > 0 && (
        <div className="border-t pt-2 space-y-1">
          {otherReviews.map((review) => (
            <div key={`${review.reviewer_email}-${review.variant}`} className="text-xs text-gray-600">
              <span className="font-medium">{review.reviewer_email.split('@')[0]}</span>:{' '}
              {review.rating ? (
                <span className={review.rating === 'use' ? 'text-green-700' : 'text-red-700'}>
                  {review.rating}
                </span>
              ) : (
                <span className="text-gray-400">unrated</span>
              )}
              {review.rating === 'use' && review.post_processing_pct > 0 && (
                <span> · {review.post_processing_pct}%</span>
              )}
              {review.changes.length > 0 && (
                <span> · {review.changes.map((c) => CHANGE_LABEL[c] ?? c).join('、')}</span>
              )}
              {review.note && <span className="italic"> — {review.note}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
